import { SHARE_CARD_SHAPES } from '../share/appShare';

/**
 * How a car travels: which picture, in which language, under what address, and
 * what to say about it.
 *
 * The counterpart to `domain/share/appShare.js`, and deliberately its shape. The
 * path is a RULE stated here and restated for the build by the script that
 * renders the cards, bound by `carShare.test.js` — two owners of one generated
 * path is how a share quietly degrades to a link with nothing in the diff to
 * explain it.
 *
 * Pure: no fetch, no `navigator`, no URL object. Loading the file is
 * `infrastructure/content/shareCardFile.js`, sharing it is
 * `application/pwa/shareCar.js`, and the canonical address it is shared under is
 * `carShareUrl` there, because that one needs a `location`.
 */

/**
 * The languages a car has actually been rendered in.
 *
 * NOT the languages the message catalogues carry, which is the difference
 * between this and the app's own card. A car card is nine parts prose to one
 * part chrome — the name, the tagline, the fun fact on the back are all
 * `prose.<lang>.json` — so the set of languages a car can travel in is exactly
 * the set of prose files that exist.
 */
export const CAR_ARTWORK_LANGUAGES = ['en'];

const FALLBACK_LANGUAGE = 'en';

/**
 * English for anything unrendered, matching `fetchProse`, which already answers
 * the same way for the same input. A 404 here would cost the picture on a share
 * that had no other reason to fail.
 */
export function carArtworkLanguage(languageCode) {
    return CAR_ARTWORK_LANGUAGES.includes(languageCode) ? languageCode : FALLBACK_LANGUAGE;
}

/**
 * The app's two shapes, plus the one only a car has.
 *
 * `unfurl` and `portrait` are imported rather than restated, because a car is
 * not a different problem: 1.91:1 is what every unfurling platform crops to, 4:5
 * is the tallest a story or a message bubble renders whole.
 *
 * `landscape` is 1.91:1 AGAIN, and the duplication is the point. `unfurl` is
 * fetched by a crawler and deliberately says less than it could — the platform
 * prints `og:description` as text beside it, so a tagline inside the picture is
 * the same sentence twice. A feed post is the opposite case: the picture is
 * UPLOADED, it carries whatever caption the poster types and nothing else. So
 * the post card is the wide composition that DOES carry the tagline, at the
 * resolution a feed on a high-density phone actually asks for — 1920 wide, where
 * the crawler's card is held to 1200 by a link preview's patience.
 *
 * 1920x1008 rather than 1920x1005: the same 1200:630 ratio as the unfurl, so one
 * composition scales into both, and both dimensions stay whole MCU blocks.
 *
 * This lives here rather than in `appShare.js` because the app's own invitation
 * has no feed post to render one for, and a shape nothing hands over is a file
 * nobody asked for.
 */
export const CAR_CARD_SHAPES = {
    ...SHARE_CARD_SHAPES,
    landscape: { key: 'landscape', width: 1920, height: 1008 },
};

/**
 * Same-origin, so the service worker can cache it and no CORS is involved.
 *
 * Under `assets/share/` rather than beside the catalogue's artwork: the content
 * build owns every file in that tree by name and deletes the ones it does not
 * recognise, and a share card is not content — it is a rendering of content.
 */
export function carCardPath(carId, languageCode, shapeKey = 'portrait') {
    const shape = CAR_CARD_SHAPES[shapeKey];

    if (!shape) {
        throw new Error(`No share card shape called ${shapeKey}`);
    }

    return `/assets/share/cars/${carId}`
        + `/card-${carArtworkLanguage(languageCode)}-${shape.width}x${shape.height}.jpg`;
}

/**
 * What the card is called once it lands in someone's Photos roll: the car's
 * name, then the product's.
 *
 * A recipient reading `card-en-1080x1350.jpg` learns nothing, and the car is the
 * thing the file is FOR. The product's name follows it so a roll with four of
 * these in it still says where they came from.
 */
export function carPictureFileName(name) {
    const carName = String(name ?? '').trim();

    return carName ? `${carName} — Legends.jpg` : 'Legends.jpg';
}

/**
 * What the share sheet says about the car, in the language on screen.
 *
 * One message key for the invitation, not three for the whole sentence. A car's
 * name joined to its own tagline by an em dash is COMPOSITION rather than copy,
 * and a key whose entire value is `{car} — {tagline}` would be identical in
 * every catalogue and trip the untranslated check in `messages.test.js` for a
 * legitimate reason.
 *
 * `text` deliberately does not carry the address. `shareImage` appends it —
 * `${text} — ${url}` — because iOS collapses a mixed `files` + `url` payload
 * back into a link share. So the invitation ends on a phrase that reads
 * correctly with an address after it rather than on a colon.
 */
export function carShareMessage({ name, tagline }, t) {
    const carName = String(name ?? '').trim();
    const line = String(tagline ?? '').trim();

    return {
        title: t('cars.share.title', { car: carName }),
        text: `${line ? `${carName} — ${line}` : carName}\n\n${t('cars.share.invitation')}`,
    };
}

/**
 * The same car, said by somebody who just won it (SPEC-010 PR-10.7).
 *
 * A second composer beside `carShareMessage` rather than a flag on it, because
 * the two sentences are not variants of one another: that one is a reader
 * passing on a car they like, and this one is a reader saying they earned it.
 * The tagline still travels, because it is the car's own line and the boast
 * reads better with the car's own words under it than with a second boast.
 *
 * The PICTURE does not change. Every car already has its three rendered shapes
 * (SPEC-005), and a fourth "won" variant would be a per-car render of a fact
 * that is not about the car — it would go stale against the copy beside it and
 * it would double a build that already rewrites forty-four JPEGs.
 *
 * Ends on a phrase that reads correctly with an address after it, for the same
 * reason `carShareMessage` does: `shareImage` appends the URL to `text` because
 * iOS collapses a mixed `files` + `url` payload back into a link share.
 */
export function carPrizeShareMessage({ name, tagline }, t) {
    const carName = String(name ?? '').trim();
    const line = String(tagline ?? '').trim();

    return {
        title: t('prize.share.title', { car: carName }),
        text: `${line ? `${carName} — ${line}` : carName}\n\n${t('prize.share.invitation')}`,
    };
}
